import { Assert } from "../Assert";
import { PartialNullable } from "../Extensions";
import { $$, IJNativeEvent } from "../JNative";
import { PermissionCacheService } from "../PermissionCacheService";
import { PropertyChange } from "../PropertyChange";
import { Property, PropertyInt32, PropertyString } from "../RadObject";
import { Evaluator } from "../Server/DotNet/SeriouslyRAD.Framework/Evaluation/Evaluator";
import { EvaluatorPropertyValues, IEvaluator } from "../Server/DotNet/SeriouslyRAD.Framework/Evaluation/IEvaluator";
import { ServiceManager } from "../ServiceManager";
import { NAMESPACE } from "./_Namespace";

export type PropertyPermissionEvaluatorPropertyValues =
    EvaluatorPropertyValues
    & {
        entityName: string;
        entityPropertyName: string;
        permission: number;
    };

export type PropertyPermissionEvaluatorProperties = {
    [name in keyof PropertyPermissionEvaluatorPropertyValues]: Property<PropertyPermissionEvaluatorPropertyValues[name]>
};

export interface IPropertyPermissionEvaluator extends IEvaluator, PropertyPermissionEvaluatorProperties
{
}

export class PropertyPermissionEvaluator extends Evaluator<PropertyPermissionEvaluatorPropertyValues> implements IPropertyPermissionEvaluator
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PropertyPermissionEvaluator";
    public static override className = "PropertyPermissionEvaluator";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PropertyPermissionEvaluatorPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                permission: 2,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public entityName!: PropertyString;
    public entityPropertyName!: PropertyString;
    public permission!: PropertyInt32;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PropertyPermissionEvaluatorPropertyValues>)
    {
        super(propertyValues);

        $$(document).on("SeriouslyRAD.PermissionCache:PermissionsChanged", { self: this }, this.onPermissionsChanged);
    }

    // #endregion

    // #region Protected Methods

    protected _entityNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entityPropertyNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _permissionChanged(change: PropertyChange<number>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PropertyPermissionEvaluatorPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.entityName = new PropertyString(this, propertyValues.entityName, this._entityNameChanged);
        this.entityPropertyName = new PropertyString(this, propertyValues.entityPropertyName, this._entityPropertyNameChanged);
        this.permission = new PropertyInt32(this, propertyValues.permission, this._permissionChanged);
    }

    public override clone(): this
    {
        const
            evaluator = super.clone() as this;

        evaluator.entityName.set(this.entityName.get());
        evaluator.entityPropertyName.set(this.entityPropertyName.get());
        evaluator.permission.set(this.permission.get());

        return evaluator;
    }
    public override evaluate()
    {
        const
            entityName = this.entityName.get(),
            entityPropertyName = this.entityPropertyName.get();

        let
            value = true; // Default true

        if (entityName && entityPropertyName)
        {
            const
                permission = this.permission.get();

            Assert.isNotNullOrUndefined(permission, "permission");

            const
                permissionCache = ServiceManager.getOptional(PermissionCacheService);

            if (permissionCache)
                value = permissionCache.hasPermissionForEntityProperty(entityName, entityPropertyName, permission);
        }

        this.value.set(value);

        return value;
    }

    // #endregion

    // #region Event Handlers

    protected onPermissionsChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.evaluate();
    }

    // #endregion

    // #endregion
}
